<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-6xl mx-auto px-4 py-6">

      <!-- VOLTAR -->
      <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors mb-6 group">
        <v-icon small color="inherit" class="group-hover:-translate-x-1 transition-transform">mdi-arrow-left</v-icon>
        <span class="text-[10px] font-bold uppercase tracking-widest">Voltar para listagem</span>
      </button>

      <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
        <div class="space-y-6 pb-24">

          <!-- SEÇÃO 1: IDENTIFICAÇÃO INSTITUCIONAL -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-office-building-marker-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO INSTITUCIONAL</h2>
            </div>

            <div class="p-6">
              <div class="flex flex-col md:flex-row gap-8 items-center md:items-start">
                <!-- LOGO -->
                <div class="flex flex-col items-center gap-2 shrink-0">
                  <div
                    class="w-40 h-40 rounded-lg border-2 border-slate-100 bg-slate-50 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors flex items-center justify-center p-4"
                    @click="$refs.fileInput.click()"
                  >
                    <img
                      v-if="data.prefecture_logo"
                      :src="'https://api.sigfrotagov.com.br/uploads/' + data.prefecture_logo"
                      class="max-w-full max-h-full object-contain"
                    />
                    <v-icon v-else color="#cbd5e0" size="48">mdi-bank-outline</v-icon>
                  </div>
                  <input type="file" ref="fileInput" @change="uploadImage" class="hidden" accept="image/*">
                  <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-orange-500 transition-colors" @click="$refs.fileInput.click()">ALTERAR BRASÃO</span>
                </div>

                <!-- CAMPOS BÁSICOS -->
                <div class="flex-1 w-full">
                  <v-row dense>
                    <v-col cols="12">
                      <label class="field-label">RAZÃO SOCIAL COMPLETA</label>
                      <v-text-field v-model="data.prefecture_social_name" placeholder="EX: PREFEITURA MUNICIPAL DE EXEMPLO..." outlined dense hide-details class="field-input font-bold uppercase" :rules="fieldRules" />
                    </v-col>
                    
                    <v-col cols="12">
                      <label class="field-label">NOME FANTASIA / CIDADE</label>
                      <v-text-field v-model="data.prefecture_fantasy_name" placeholder="EX: CIDADE EXEMPLO..." outlined dense hide-details class="field-input font-bold uppercase" />
                    </v-col>

                    <v-col cols="12" md="6">
                      <label class="field-label">CNPJ</label>
                      <v-text-field v-model="data.prefecture_cnpj" v-mask-cnpj placeholder="00.000.000/0000-00" outlined dense hide-details class="field-input font-bold" :rules="fieldRules" :error="!validCnpj(data.prefecture_cnpj)" />
                    </v-col>

                    <v-col cols="12" md="6">
                      <label class="field-label">INSCRIÇÃO ESTADUAL</label>
                      <v-text-field v-model="data.prefecture_ie" placeholder="ISENTO" outlined dense hide-details class="field-input font-bold" />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </section>

          <!-- SEÇÃO 2: CONTATO E FATURAMENTO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-cash-register</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">CANAIS DE CONTATO E OPERAÇÃO</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="5">
                  <label class="field-label">E-MAIL ADMINISTRATIVO</label>
                  <v-text-field v-model="data.prefecture_mail" placeholder="ADMIN@PREFEITURA.GOV.BR" outlined dense hide-details class="field-input lowercase" prepend-inner-icon="mdi-email-outline" />
                </v-col>
                <v-col cols="12" md="3">
                  <label class="field-label">CELULAR / WHATSAPP</label>
                  <v-text-field v-model="data.prefecture_cel" v-mask-phone.br placeholder="(00) 00000-0000" outlined dense hide-details class="field-input font-bold" prepend-inner-icon="mdi-cellphone" />
                </v-col>
                <v-col cols="12" md="4">
                  <label class="field-label">TELEFONE FIXO</label>
                  <v-text-field v-model="data.prefecture_tel" v-mask-phone.br placeholder="(00) 0000-0000" outlined dense hide-details class="field-input font-bold" prepend-inner-icon="mdi-phone-outline" />
                </v-col>

                <v-col cols="12" class="mt-6">
                  <div class="bg-slate-50 p-6 rounded-xl border border-slate-100 border-l-4 border-l-orange-500 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div class="flex-1">
                      <h4 class="text-[10px] font-black text-slate-800 uppercase tracking-widest mb-1">Metodologia de Faturamento</h4>
                      <p class="text-[11px] text-slate-500 font-medium leading-relaxed m-0">Define como as ordens de pagamento serão agrupadas no financeiro.</p>
                    </div>
                    <div class="w-full md:w-64">
                      <v-autocomplete
                        v-model="data.billing_type"
                        :items="['POR VEICULO', 'POR SECRETARIA']"
                        outlined dense hide-details
                        class="field-input-dark"
                        prepend-inner-icon="mdi-invoice-text-multiple-outline"
                      />
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- SEÇÃO 3: LOCALIZAÇÃO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-map-marker-radius-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ENDEREÇO E LOGRADOURO</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12" md="3">
                  <label class="field-label">CEP</label>
                  <v-text-field v-model="data.prefecture_postal_code" v-mask-cep placeholder="00.000-000" outlined dense hide-details class="field-input font-bold" prepend-inner-icon="mdi-map-search-outline" @blur="getCep(data.prefecture_postal_code)" />
                </v-col>
                <v-col cols="12" md="7">
                  <label class="field-label">LOGRADOURO</label>
                  <v-text-field v-model="data.prefecture_street" placeholder="AVENIDA / RUA" outlined dense hide-details class="field-input uppercase" prepend-inner-icon="mdi-road" />
                </v-col>
                <v-col cols="12" md="2">
                  <label class="field-label text-center">Nº</label>
                  <v-text-field v-model="data.prefecture_number" placeholder="S/N" outlined dense hide-details class="field-input text-center font-bold" />
                </v-col>

                <v-col cols="12" md="4" class="mt-4">
                  <label class="field-label">COMPLEMENTO</label>
                  <v-text-field v-model="data.prefecture_complement" placeholder="SALA / BLOCO" outlined dense hide-details class="field-input uppercase" />
                </v-col>
                <v-col cols="12" md="3" class="mt-4">
                  <label class="field-label">BAIRRO</label>
                  <v-text-field v-model="data.prefecture_district" placeholder="CENTRO" outlined dense hide-details class="field-input uppercase" />
                </v-col>
                <v-col cols="12" md="3" class="mt-4">
                  <label class="field-label">CIDADE</label>
                  <v-text-field v-model="data.prefecture_city" placeholder="CIDADE" outlined dense hide-details class="field-input uppercase font-bold" />
                </v-col>
                <v-col cols="12" md="2" class="mt-4">
                  <label class="field-label text-center">UF</label>
                  <v-text-field v-model="data.prefecture_state" placeholder="UF" outlined dense hide-details class="field-input text-center uppercase font-bold" maxlength="2" />
                </v-col>
              </v-row>
            </div>
          </section>

          <!-- SEÇÃO 4: CONFIGURAÇÕES DE SEGURANÇA -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-shield-check-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">SEGURANÇA E CONFORMIDADE</h2>
            </div>

            <div class="p-6">
               <div 
                 class="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors"
                 :class="data.block_expired_cnh ? 'bg-orange-50 border border-orange-100' : 'bg-slate-50 border border-slate-100'"
                 @click="data.block_expired_cnh = !data.block_expired_cnh"
               >
                 <v-switch v-model="data.block_expired_cnh" color="orange" inset hide-details class="mt-0" @click.stop />
                 <div class="flex-1">
                   <h4 class="text-[10px] font-black uppercase tracking-widest" :class="data.block_expired_cnh ? 'text-orange-900' : 'text-slate-800'">BLOQUEAR POR CNH VENCIDA</h4>
                   <p class="text-[11px] font-medium m-0 opacity-70">Impede abastecimentos caso a CNH do condutor esteja fora da validade.</p>
                 </div>
               </div>
            </div>

            <!-- FOOTER AÇÕES -->
            <div class="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-4">
              <button
                type="button"
                @click="$router.back()"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader || !validCnpj(data.prefecture_cnpj)"
                style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
              </button>
            </div>
          </section>
        </div>
      </v-form>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      prefecture_id: null,
      prefecture_logo: "",
      prefecture_social_name: "",
      prefecture_fantasy_name: "",
      prefecture_cnpj: "",
      prefecture_ie: "",
      prefecture_mail: "",
      prefecture_cel: "",
      prefecture_tel: "",
      prefecture_postal_code: "",
      prefecture_street: "",
      prefecture_number: "",
      prefecture_complement: "",
      prefecture_district: "",
      prefecture_city: "",
      prefecture_state: "",
      billing_type: "POR SECRETARIA",
      block_expired_cnh: false,
    },
  }),
  created() {
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`prefectures/${this.id}`).then((r) => {
        this.data = r.data[0];
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validCnpj(v) {
      if (!v) return false;
      let cnpj = v.replace(/\D/gim, "");
      if (cnpj.length !== 14) return false;
      if (/^(\d)\1+$/.test(cnpj)) return false;
      let t = cnpj.length - 2, d = cnpj.substring(t), d1 = parseInt(d.charAt(0)), d2 = parseInt(d.charAt(1)),
        calc = (x) => {
          let n = cnpj.substring(0, x), y = x - 7, s = 0;
          for (let i = x; i >= 1; i--) { s += n.charAt(x - i) * y--; if (y < 2) y = 9; }
          let r = 11 - (s % 11); return r > 9 ? 0 : r;
        };
      return calc(t) === d1 && calc(t + 1) === d2;
    },
    validate() {
      if (this.$refs.form.validate() && this.validCnpj(this.data.prefecture_cnpj)) {
        this.createOrUpdate();
      } else {
        this.snack.show = true; this.snack.color = "warning"; this.snack.message = "Atenção: Verifique os campos e o CNPJ!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      const method = this.id ? 'put' : 'post';
      const endpoint = this.id ? `/prefectures/${this.id}` : "/prefectures";
      this.$http[method](endpoint, this.data).then((r) => {
        this.snack.show = true; this.snack.message = r.data.result; this.snack.color = "success";
        setTimeout(() => this.$router.push("/prefectures-manager"), 1500);
      }).catch((e) => {
        this.snack.show = true; this.snack.message = e.message; this.snack.color = "error";
        this.loader = false; this.$store.dispatch("setLoader", false);
      });
    },
    uploadImage(event) {
      if (!event.target.files[0]) return;
      this.$store.dispatch("setLoader", true);
      const fd = new FormData(); fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http.post("/upload", fd).then((r) => {
        this.data.prefecture_logo = r.data.cover; this.$store.dispatch("setLoader", false);
      }).catch((e) => {
        alert("Erro no upload: " + e.message); this.$store.dispatch("setLoader", false);
      });
    },
    getCep(cep) {
      if (cep && cep.length == 10) {
        this.$store.dispatch("setLoader", true);
        const cleanCep = cep.replace(/\D/g, "");
        this.$http.get("https://viacep.com.br/ws/" + cleanCep + "/json/").then((response) => {
          if (!response.data.erro) {
            this.data.prefecture_street = response.data.logradouro;
            this.data.prefecture_district = response.data.bairro;
            this.data.prefecture_city = response.data.localidade;
            this.data.prefecture_state = response.data.uf;
          }
          this.$store.dispatch("setLoader", false);
        }).catch(() => { this.$store.dispatch("setLoader", false); });
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.field-input-dark >>> .v-input__control .v-input__slot {
  background-color: #333333 !important;
  border-radius: 6px !important;
}

.field-input-dark >>> .v-input__control .v-input__slot fieldset {
  border-color: #444444 !important;
}

.field-input-dark >>> input,
.field-input-dark >>> .v-select__selection {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 12px !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
